import { useRef, forwardRef } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useLocation, useHistory } from 'react-router-dom';
import {
    BREAKPOINTS,
    TAB_ALIGNMENT,
    TAB_VARIANT,
    Footer,
    SideNav,
    Tabs,
    UtilityHeader,
    colors,
    typography,
    Button,
} from '@manulife/mux';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import WrappedLogo from './WrappedLogo';
import { GetHeaderRightItems, GetFooterItems } from './util';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { TransferOutWithToken, DESTINATIONS } from '../Shared/ReducerActions/TransferOutWithToken';
import { APP_CONSTANTS } from '../../utils/AppConstants';

const Container = styled.div`
    position: absolute;
    top: ${({ pageFrameHidden }) => (pageFrameHidden ? '0px' : '40px')};
    left: ${({ pageFrameHidden }) => (pageFrameHidden ? '0px' : '80px')};
    bottom: 0px;
    right: 0px;
    overflow-x: hidden;
    overflow-y: auto;

    @media (max-width: ${BREAKPOINTS.MAX_1024}px) {
        top: ${({ pageFrameHidden }) => (pageFrameHidden ? '0px' : '60px')};
        left: 0;
    }

    @media print {
        position: static;
        overflow: visible;
        height: 100%;
        top: 0px;
    }
`;

const ContainerWrapper = forwardRef((props, ref) => <Container ref={ref} {...props} />);

// TODO: There's a MUX bug where SideNav is longer than it should be.
// See: https://github.com/manulife-innersource/mux-core/issues/2099
// This restricts it to the height it should be, and can be removed when that is fixed
const SideNavWrapper = styled.div`
    & > nav {
        height: calc(100vh - 40px);
        @media (max-width: ${BREAKPOINTS.MAX_1024}px) {
            height: 60px;
        }
    }
`;
const BannerText = styled.div`
    p {
        width: 90%;
        margin: 10px auto;
        color: ${colors.light_1_dark_navy};
        font-size: 44px;
        font-weight: ${typography.fontWeightLight};
    }
    em {
        font-weight: ${typography.fontWeightNormal};
        font-family: ${typography.fontFamily};
    }
`;

const Main = styled.main`
    min-height: calc(100% - 100px);
    width: 90%;
    margin: 0 auto;
    padding-bottom: 40px;
`;

const BackButtonContainer = styled.div`
    margin-top: 40px;
    button {
        padding-left: 0;
    }
    @media (max-width: ${BREAKPOINTS.MAX_768}px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const LAYOUT_VARIANT = {
    MAIN: 'main',
    BANNERLESS: 'bannerless',
};

const Layout = ({ children, pageTitle = 'Member Profile', id = 'layout', layoutVariant = LAYOUT_VARIANT.MAIN }) => {
    const { isLoadingMemberData, member, language, isOtip, ctx } = useShallowEqualSelector((state) => ({
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        member: state.MemberData.member,
        language: state.Authentication.language,
        isOtip: state.Context.isOtip,
        ctx: state.Context.ctx,
    }));

    const pageContainerRef = useRef(null);
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const leftScrollBarLabel = useAEMContent(FRAGMENT_MP.landing, 'mobileLeftTabScrollButtonAriaLabel');
    const rightScrollBarLabel = useAEMContent(FRAGMENT_MP.landing, 'mobileRightTabScrollButtonAriaLabel');
    const welcomeMessage = useAEMContent(FRAGMENT_MP.landing, 'welcomeMessage', {
        parseHTML: true,
        replaceText: [{ key: '{fName}', value: <em>{member.firstName}</em> }],
    });
    const personalInfoTab = useAEMContent(FRAGMENT_MP.landing, 'personalInfoTab');
    const myFamilyTab = useAEMContent(FRAGMENT_MP.landing, 'myFamilyTab');
    const ariaManulifeLogoText = useAEMContent(FRAGMENT_MP.common, 'ariaManulifeLogo');
    const continueToClaimsButton = useAEMContent(FRAGMENT_MP.landing, 'continueToClaimsButton');

    const destination = sessionStorage.getItem(APP_CONSTANTS.MPROFILE_ORIGIN) || DESTINATIONS.planMember;
    const claimStep = sessionStorage.getItem(APP_CONSTANTS.MPROFILE_CLAIMSTEP);
    const tabContentBoth = [
        {
            id: 'personalTabButton',
            value: '/gb/member-profile-2/personal',
            label: personalInfoTab,
        },
        {
            id: 'familyTabButton',
            value: '/gb/member-profile-2/family',
            label: myFamilyTab,
        },
    ];
    const tabContentPersonal = [
        {
            id: 'personalTabButton',
            value: '/gb/member-profile-2/personal',
            label: personalInfoTab,
        },
    ];

    const pageFrameHidden = isOtip && ctx?.origin === 'mobile';

    return (
        <>
            <Helmet
                htmlAttributes={{
                    lang: language,
                }}
            >
                <title>Manulife - {pageTitle}</title>
            </Helmet>
            {!pageFrameHidden && (
                <>
                    <UtilityHeader>{GetHeaderRightItems(language)}</UtilityHeader>
                    <SideNavWrapper>
                        <SideNav
                            brandLogoProps={{
                                onClick: () => dispatch(TransferOutWithToken(destination, '', claimStep, '_self')),
                                alt: ariaManulifeLogoText,
                            }}
                        />
                    </SideNavWrapper>
                </>
            )}
            <ContainerWrapper id={id} pageFrameHidden={pageFrameHidden} ref={pageContainerRef}>
                <WrappedLogo pageContainerRef={pageContainerRef} />

                {layoutVariant === LAYOUT_VARIANT.MAIN && (
                    <>
                        <BannerText>{!isLoadingMemberData && <>{welcomeMessage}</>}</BannerText>
                        <Tabs
                            tabItems={destination === 'gbClaims' ? tabContentPersonal : tabContentBoth}
                            variant={TAB_VARIANT.GREY}
                            tabAlignment={TAB_ALIGNMENT.LEFT}
                            selectedTab={location.pathname}
                            scrollButtonAriaLabel={{
                                left: leftScrollBarLabel,
                                right: rightScrollBarLabel,
                            }}
                            onChange={(value) => {
                                history.push(value);
                            }}
                            customStyle={{
                                tabsWrapperStyle: {
                                    padding: '0 5%',
                                },
                            }}
                        />
                    </>
                )}
                <Main>
                    {children}
                    {destination === 'gbClaims' && (
                        <BackButtonContainer>
                            <Button
                                variant={Button.VARIANT.TERTIARY}
                                onClick={() => dispatch(TransferOutWithToken(destination, '', claimStep, '_self'))}
                            >
                                {continueToClaimsButton}
                            </Button>
                        </BackButtonContainer>
                    )}
                </Main>

                {!pageFrameHidden && (
                    <Footer
                        id="footer"
                        items={GetFooterItems(language)}
                        // This is to avoid an accessibility issue with the copyright
                        // element having the same aria-label as its text
                        copyrightYearAria={null}
                    />
                )}
            </ContainerWrapper>
        </>
    );
};

export default Layout;
